import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Button,
} from "@mui/material"
import {
  ButtonWrapper,
  ModalCard,
  ModalContent,
  ModalHeader,
  Spacer,
} from "./EditPoLineModal.styles"
import { useMemo, useState } from "react"
import { validateCurrency } from "../../util/validateCurrency"
import FormTextbox from "../FormTextBox/FormTextbox"
import Big from "big.js"

export interface DataToPopulate {
  tempLineIdentifier: string
  lineType: string | null | undefined
  lineAmount: number | null | undefined
  taxAmount: number | null | undefined
  codeCombination: string | null | undefined
  quantity: number | null | undefined
  price: number | null | undefined
  unitOfMeasure: string | null | undefined
  shipmentNumber: string | null | undefined
  description: string | null | undefined
  taxCode: string
}

export interface Props {
  open: boolean
  onClose: () => void
  onSave: (val: any) => void
  existingData: DataToPopulate
  taxCodes: Array<string>
}

export default function EditPoLineModal(props: Props) {
  const [inputStates, setInputStates] = useState(props.existingData)

  function handleInputChange(key: string, value: string | number | undefined) {
    setInputStates((prevState) => {
      return {
        ...prevState,
        [key]: value,
      }
    })
  }

  const validateLineAmount = useMemo(() => {
    try {
      const preparedLineAmount = Big(inputStates.lineAmount || 0)
      const preparedQuantity = Big(inputStates.quantity || 0)
      const preparedPrice = Big(inputStates.price || 0)
      const expectedTotal = preparedQuantity.times(preparedPrice)
      const leeway = Big("1.0")
      const leeway_negative = Big("-1.0")
      const difference = preparedLineAmount.sub(expectedTotal)

      return difference.lte(leeway) && difference.gte(leeway_negative)
        ? "ok"
        : "error"
    } catch (err: unknown) {
      return "error"
    }
  }, [inputStates.lineAmount, inputStates.quantity, inputStates.price])

  function handleSave() {
    props.onSave(inputStates)
  }

  return (
    <Modal open={props.open} onClose={props.onClose}>
      <ModalCard
        sx={{
          transform: "translateX(-50%)",
          top: "unset",
          bottom: 0,
        }}
      >
        <ModalHeader>Invoice line details</ModalHeader>
        <ModalContent>Edit the values of this invoice line</ModalContent>
        <Box
          sx={{
            display: "flex",
            padding: "24px, 0px",
            alignItems: "flex-start",
            gap: "48px",
            alignSelf: "stretch",
          }}
        >
          <Box
            sx={{
              display: "flex",
              padding: "0px",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "24px",
              flex: 1,
            }}
          >
            <FormControl fullWidth>
              <InputLabel>Line type</InputLabel>
              <Select
                value={inputStates.lineType}
                onChange={(e, v) =>
                  handleInputChange("lineType", e.target.value || undefined)
                }
                label="Line type"
              >
                <MenuItem value={"Item"}>Item</MenuItem>
                <MenuItem value={"Tax"}>Tax</MenuItem>
              </Select>
            </FormControl>
            <FormTextbox
              label={"Line amount"}
              value={inputStates.lineAmount || ""}
              onChange={(e) => {
                if (validateCurrency(e.target.value)) {
                  handleInputChange("lineAmount", e.target.value)
                }
              }}
              validationState={validateLineAmount}
            />
            <FormTextbox
              label={"Quantity"}
              value={inputStates.quantity || ""}
              onChange={(e) => {
                if (validateCurrency(e.target.value)) {
                  handleInputChange("quantity", e.target.value)
                }
              }}
              validationState={validateLineAmount}
            />
            <FormTextbox
              label={"Price"}
              value={inputStates.price || ""}
              onChange={(e) => {
                if (validateCurrency(e.target.value)) {
                  handleInputChange("price", e.target.value)
                }
              }}
              validationState={validateLineAmount}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              padding: "0px",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "24px",
              flex: 1,
            }}
          >
            <FormTextbox
              label={"Tax amount"}
              value={inputStates.taxAmount || ""}
              onChange={(e) => {
                if (validateCurrency(e.target.value)) {
                  handleInputChange("taxAmount", e.target.value)
                }
              }}
              validationState={"default"}
            />

            <FormControl fullWidth>
              <InputLabel>Tax code</InputLabel>
              <Select
                label="Tax code"
                value={inputStates.taxCode}
                onChange={(e, v) =>
                  handleInputChange("taxCode", e.target.value || undefined)
                }
              >
                {props.taxCodes.map((taxCode) => (
                  <MenuItem key={taxCode} value={taxCode}>
                    {taxCode}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormTextbox
              label={"Code combination"}
              value={inputStates.codeCombination}
              onChange={(e) =>
                handleInputChange("codeCombination", e.target.value)
              }
              validationState={"default"}
            />
            <FormTextbox
              label={"Unit of measure"}
              value={inputStates.unitOfMeasure}
              onChange={(e) =>
                handleInputChange("unitOfMeasure", e.target.value)
              }
              validationState={"default"}
            />
            <FormTextbox
              label={"Shipment number"}
              value={inputStates.shipmentNumber}
              onChange={(e) =>
                handleInputChange("shipmentNumber", e.target.value)
              }
              validationState={"default"}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              padding: "0px",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "24px",
              flex: 1,
            }}
          >
            <TextField
              variant={"outlined"}
              label={"Invoice Description"}
              fullWidth
              multiline
              rows={10}
              value={inputStates.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
            />
          </Box>
        </Box>
        <ButtonWrapper sx={{padding: "16px 0"}}>
          <Spacer />
          <Button
              variant="outlined"
              onClick={props.onClose}
          >
              Cancel
          </Button>
          <Button
              variant="contained"
              onClick={handleSave}
          >
              Save changes
          </Button>
        </ButtonWrapper>
      </ModalCard>
    </Modal>
  )
}
